import Cookies, { type CookieAttributes } from 'js-cookie';

import { fg } from '@atlaskit/platform-feature-flags/fg';

import { getBscGlobalState, Logger, sendPackageOperationalEvent } from '../../../common/utils';
import { getItemCategoryFromPassedCache } from '../../../common/utils/item-processing';
import { findStorageTarget, WebStorageType } from '../../../services/web-storage-service';
import {
	CategoryStorageType,
	PreferenceCategory,
	type StorageCleanupDeletionMode,
	type StorageCleanupItem,
	type StorageCleanupResult,
	type StorageCleanupSkippedType,
	type UncategorizedStorageItem,
} from '../../../types';
import { getPreferences } from '../../preferences-controls/get-preferences';

function isCategoryRevoked({
	category,
	preferences,
}: {
	category: PreferenceCategory;
	preferences: Awaited<ReturnType<typeof getPreferences>>;
}): boolean {
	if (category === PreferenceCategory.StrictlyNecessary) {
		return false;
	}
	if (!preferences) {
		return false;
	}
	return !preferences[category];
}

function yieldToMain(): Promise<void> {
	return new Promise((resolve) => setTimeout(resolve, 0));
}

const ITEMS_PER_YIELD = 50;
// Per-run enumeration cap. Yielding to the main thread every ITEMS_PER_YIELD items is independent
// of the limit, so the loop stays cooperative however high this is set.
const ENUMERATION_LIMIT = 10000;

// Runs revoking more than this are pathological (a bug or misconfigured page), not ordinary
// high-storage pages. Flag them so a per-run blowout is caught before live deletes are enabled.
// Set well under ENUMERATION_LIMIT so it stays meaningful.
export const REVOKED_COUNT_CEILING = 1000;

// `deleteDatabase` completes only once every other connection to that database closes, which the
// browser asks for by firing `versionchange` on each of them. Nothing obliges a holder to listen, so
// the wait needs a bound. Confirmations run concurrently, making this the cost to a run rather than
// the cost per database.
const IDB_DELETE_CONFIRM_TIMEOUT_MS = 500;

function isRunningInFrame(): boolean {
	try {
		return window.top !== window.self;
	} catch {
		// A cross-origin read of `window.top` throws, which itself proves we are framed.
		return true;
	}
}

/**
 * Resolves for every outcome rather than rejecting, so one database cannot fail a whole run.
 *
 * `blocked` is deliberately not handled: it is not an outcome. Another open connection leaves the
 * request pending, and `success` still arrives if those connections close, so treating `blocked` as
 * a failure would report a delete that then went through as undeleted. The deadline decides instead
 * — unblocked in time counts as deleted, still blocked when it expires does not.
 */
function deleteDatabaseAndConfirm(idb: IDBFactory, name: string): Promise<boolean> {
	return new Promise((resolve) => {
		let settled = false;
		let timeoutId: ReturnType<typeof setTimeout> | undefined;
		const settle = (deleted: boolean) => {
			if (settled) {
				return;
			}
			settled = true;
			if (timeoutId !== undefined) {
				clearTimeout(timeoutId);
			}
			resolve(deleted);
		};

		const request = idb.deleteDatabase(name);
		request.onsuccess = () => settle(true);
		request.onerror = () => {
			Logger.warn(
				`cleanupStorage: failed to delete IndexedDB "${name}": ${request.error?.message ?? ''}`,
			);
			settle(false);
		};
		timeoutId = setTimeout(() => settle(false), IDB_DELETE_CONFIRM_TIMEOUT_MS);
	});
}

/**
 * A cookie is identified by name + domain + path, and `document.cookie` exposes only the name. An
 * expiry therefore has to guess the other two, and a guess that misses is silently accepted by the
 * browser, so reading the cookie back is the only way to know it went away.
 *
 * Two scopes are tried: host-only, then the served host as an explicit `domain`, because scoping to
 * `Domain=<current host>` is a common library default that a host-only expiry cannot match.
 *
 * Known limitations, all reported as `false` rather than papered over:
 *
 * - **No domain above the served host is tried.** A cookie on a parent or registrable domain — where
 *   the vendor tags we embed (`_ga`, `_fbp`, `_gcl_au`) and our own cross-property cookies live — is
 *   out of reach.
 * - **Non-root paths are never tried.** js-cookie defaults to `path=/`.
 * - **`HttpOnly` cookies are invisible here**, so they are never even enumerated.
 */
function deleteCookieAndConfirm(key: string): boolean {
	// `__Secure-`/`__Host-` names are rejected unless the expiring `Set-Cookie` also carries
	// `Secure`. It is not part of a cookie's identity so it is harmless on ordinary cookies, but a
	// non-secure origin ignores the whole header, so only send it where it can take effect.
	const secure: CookieAttributes = window.location.protocol === 'https:' ? { secure: true } : {};

	// `atl-set-cookie` marks both expiries below as the package's own writes, which is what the
	// `document.cookie` setter override checks before passing one through. Without it the override
	// consent-checks the expiry against the very category the cookie is being deleted for, takes its
	// blocked branch, and never calls the original setter.
	Cookies.remove(key, { ...secure, 'atl-set-cookie': true });
	if (Cookies.get(key) === undefined) {
		return true;
	}

	// Still there, so the cookie carries an explicit `Domain`. Two names make even the served-host
	// attempt pointless: a `__Host-` cookie must be host-only, and on a single-label host
	// (`localhost`, an intranet name) `.localhost` matches nothing.
	const { hostname } = window.location;
	if (key.startsWith('__Host-') || hostname.split('.').length < 2) {
		return false;
	}

	Cookies.remove(key, { ...secure, domain: `.${hostname}`, 'atl-set-cookie': true });
	return Cookies.get(key) === undefined;
}

export async function cleanupStorage(): Promise<StorageCleanupResult> {
	// One read per run, outside the try so the catch path agrees. `fg()` reads gate config that can
	// change mid-run and this function's p99 is seconds, so re-reading risks a run that deletes
	// while reporting `dry-run`.
	const deletionMode: StorageCleanupDeletionMode = fg(
		'platform_bsc_cleanup_revoked_storage_live_delete',
	)
		? 'live'
		: 'dry-run';
	const isLiveDelete = deletionMode === 'live';

	try {
		const cleanupStartTime = performance.now();
		const globalState = getBscGlobalState();
		const preferences = await getPreferences();
		const localStorage = findStorageTarget(WebStorageType.LocalStorage);
		const sessionStorage = findStorageTarget(WebStorageType.SessionStorage);
		const idb = window.indexedDB;
		const productName = globalState.product;

		const [localStorageCache, sessionStorageCache, cookieCache, indexedDBCache] = await Promise.all(
			[
				globalState.loadCache(CategoryStorageType.LocalStorage),
				globalState.loadCache(CategoryStorageType.SessionStorage),
				globalState.loadCache(CategoryStorageType.Cookies),
				globalState.loadCache(CategoryStorageType.IndexedDB),
			],
		);

		const revokedItems: StorageCleanupItem[] = [];
		const uncategorizedItems: UncategorizedStorageItem[] = [];
		let hitEnumerationLimit = false;
		let itemsProcessed = 0;
		let deletionCount = 0;

		const reportCoverage = fg('platform_bsc_cleanup_coverage_telemetry');

		const skippedTypes: StorageCleanupSkippedType[] = [];
		const undeletedItems: StorageCleanupItem[] = [];
		const inFrame = reportCoverage ? isRunningInFrame() : undefined;
		const pendingIdbConfirmations: Promise<void>[] = [];

		// localStorage
		const shouldDeleteWebStorage =
			!globalState.disableWebStorageConsentChecks && globalState.webStorageEnabled;
		if (!localStorageCache) {
			skippedTypes.push({ storageType: 'localStorage', reason: 'cacheUnavailable' });
		} else {
			// Cleanup is an internal operation, so it works on the store the wrapper wraps rather than
			// through the wrapper: the read-back below is untrapped, where the wrapper's `getItem`
			// emits `usedAtlBrowserStorageGetItem` on every call. Keys are taken up front so a removal
			// cannot shift the items still to be visited. `Object.keys` would be shorter but omits any
			// item whose name collides with a `Storage` own property — WebKit hides a stored `length`
			// from it, where `key(i)` still reports it.
			// eslint-disable-next-line @atlaskit/platform/no-direct-web-storage-usage -- existing usage
			const localStore = localStorage.storage;
			const localKeys = Array.from({ length: localStore.length }, (_, i) =>
				localStore.key(i),
			).filter((key): key is string => key !== null);
			for (const key of localKeys) {
				if (itemsProcessed >= ENUMERATION_LIMIT) {
					hitEnumerationLimit = true;
					break;
				}
				if ((itemsProcessed + 1) % ITEMS_PER_YIELD === 0) {
					await yieldToMain();
				}
				const category = getItemCategoryFromPassedCache(key, localStorageCache, productName);
				if (category === null || category === PreferenceCategory.Unknown) {
					uncategorizedItems.push({ key, storageType: 'localStorage' });
				} else if (isCategoryRevoked({ category, preferences })) {
					const item: StorageCleanupItem = { key, storageType: 'localStorage', category };
					revokedItems.push(item);
					if (shouldDeleteWebStorage && isLiveDelete) {
						localStore.removeItem(key);
						const removed = localStore.getItem(key) === null;
						item.deleted = removed;
						if (!removed) {
							undeletedItems.push(item);
						}
						deletionCount++;
					}
				}
				itemsProcessed++;
			}
		}

		// sessionStorage
		if (hitEnumerationLimit) {
			skippedTypes.push({ storageType: 'sessionStorage', reason: 'enumerationLimit' });
		} else if (!sessionStorageCache) {
			skippedTypes.push({ storageType: 'sessionStorage', reason: 'cacheUnavailable' });
		} else {
			// eslint-disable-next-line @atlaskit/platform/no-direct-web-storage-usage -- existing usage
			const sessionStore = sessionStorage.storage;
			const sessionKeys = Array.from({ length: sessionStore.length }, (_, i) =>
				sessionStore.key(i),
			).filter((key): key is string => key !== null);
			for (const key of sessionKeys) {
				if (itemsProcessed >= ENUMERATION_LIMIT) {
					hitEnumerationLimit = true;
					break;
				}
				if ((itemsProcessed + 1) % ITEMS_PER_YIELD === 0) {
					await yieldToMain();
				}
				const category = getItemCategoryFromPassedCache(key, sessionStorageCache, productName);
				if (category === null || category === PreferenceCategory.Unknown) {
					uncategorizedItems.push({ key, storageType: 'sessionStorage' });
				} else if (isCategoryRevoked({ category, preferences })) {
					const item: StorageCleanupItem = { key, storageType: 'sessionStorage', category };
					revokedItems.push(item);
					if (shouldDeleteWebStorage && isLiveDelete) {
						sessionStore.removeItem(key);
						const removed = sessionStore.getItem(key) === null;
						item.deleted = removed;
						if (!removed) {
							undeletedItems.push(item);
						}
						deletionCount++;
					}
				}
				itemsProcessed++;
			}
		}

		// cookies
		if (hitEnumerationLimit) {
			skippedTypes.push({ storageType: 'cookie', reason: 'enumerationLimit' });
		} else if (!cookieCache) {
			skippedTypes.push({ storageType: 'cookie', reason: 'cacheUnavailable' });
		} else {
			for (const key of Object.keys(Cookies.get())) {
				if (itemsProcessed >= ENUMERATION_LIMIT) {
					hitEnumerationLimit = true;
					break;
				}
				if ((itemsProcessed + 1) % ITEMS_PER_YIELD === 0) {
					await yieldToMain();
				}
				const category = getItemCategoryFromPassedCache(key, cookieCache, productName);
				if (category === null || category === PreferenceCategory.Unknown) {
					uncategorizedItems.push({ key, storageType: 'cookie' });
				} else if (isCategoryRevoked({ category, preferences })) {
					const item: StorageCleanupItem = { key, storageType: 'cookie', category };
					revokedItems.push(item);
					// There's no corresponding `shouldDeleteWebStorage` for cookies as they have no reporting-only mode.
					if (isLiveDelete) {
						item.deleted = deleteCookieAndConfirm(key);
						if (!item.deleted) {
							undeletedItems.push(item);
						}
						deletionCount++;
					}
				}
				itemsProcessed++;
			}
		}

		// IndexedDB
		const shouldDeleteIdb =
			!globalState.disableIndexedDbConsentChecks && globalState.indexedDbEnabled;
		if (hitEnumerationLimit) {
			skippedTypes.push({ storageType: 'indexedDB', reason: 'enumerationLimit' });
		} else if (!indexedDBCache) {
			skippedTypes.push({ storageType: 'indexedDB', reason: 'cacheUnavailable' });
		} else if (typeof idb?.databases !== 'function') {
			// Safari and Firefox do not implement `indexedDB.databases()`.
			skippedTypes.push({ storageType: 'indexedDB', reason: 'unsupported' });
		} else {
			try {
				const dbs = await idb.databases();
				for (const db of dbs) {
					if (itemsProcessed >= ENUMERATION_LIMIT) {
						hitEnumerationLimit = true;
						break;
					}
					if ((itemsProcessed + 1) % ITEMS_PER_YIELD === 0) {
						await yieldToMain();
					}
					const name = db.name;
					if (name === undefined) {
						continue;
					}
					const category = getItemCategoryFromPassedCache(name, indexedDBCache, productName);
					if (category === null || category === PreferenceCategory.Unknown) {
						uncategorizedItems.push({ key: name, storageType: 'indexedDB' });
					} else if (isCategoryRevoked({ category, preferences })) {
						const item: StorageCleanupItem = { key: name, storageType: 'indexedDB', category };
						revokedItems.push(item);
						if (shouldDeleteIdb && isLiveDelete) {
							// Collected, not awaited inline: serial waits would make the worst case the sum of
							// the per-database timeouts instead of the longest one.
							pendingIdbConfirmations.push(
								deleteDatabaseAndConfirm(idb, name).then((deleted) => {
									item.deleted = deleted;
									if (!deleted) {
										undeletedItems.push(item);
									}
								}),
							);
							deletionCount++;
						}
					}
					itemsProcessed++;
				}
			} catch {
				// idb.databases() unavailable or threw — skip IDB entirely
				skippedTypes.push({ storageType: 'indexedDB', reason: 'error' });
			}
		}

		if (pendingIdbConfirmations.length > 0) {
			await Promise.all(pendingIdbConfirmations);
		}

		if (deletionCount > 0) {
			sendPackageOperationalEvent({
				action: 'storageCleanupDeleted',
				attributes: {
					deletedCount: deletionCount - undeletedItems.length,
					undeletedCount: undeletedItems.length,
				},
			});
		}

		sendPackageOperationalEvent({
			action: 'storageCleanupCategorized',
			attributes: {
				durationMs: Math.round(performance.now() - cleanupStartTime),
				deletionMode,
				revokedCount: revokedItems.length,
				revokedItems: revokedItems.map(
					({ storageType, key, category }) => `${storageType} ${key} ${category}`,
				),
				uncategorizedCount: uncategorizedItems.length,
				uncategorizedItems: uncategorizedItems.map(
					({ storageType, key }) => `${storageType} ${key}`,
				),
				hitEnumerationLimit,
				hitRevokedCountCeiling: revokedItems.length > REVOKED_COUNT_CEILING,
				...(isLiveDelete
					? {
							undeletedCount: undeletedItems.length,
							undeletedItems: undeletedItems.map(({ storageType, key }) => `${storageType} ${key}`),
						}
					: {}),
				...(reportCoverage
					? {
							// Empty rather than omitted, so an absent attribute means "gate off" and not
							// "full coverage".
							skippedTypes: skippedTypes
								.map(({ storageType, reason }) => `${storageType}:${reason}`)
								.join(','),
							inFrame,
						}
					: {}),
			},
		});

		return {
			deletionMode,
			revokedItems,
			uncategorizedItems,
			hitEnumerationLimit,
			...(reportCoverage ? { skippedTypes, inFrame } : {}),
		};
	} catch (error: any) {
		Logger.errorWithOperationalEvent({
			action: 'storageCleanupError',
			message: `cleanupStorage failed: ${error?.message ?? 'no message'}`,
		});
		return {
			deletionMode,
			revokedItems: [],
			uncategorizedItems: [],
			hitEnumerationLimit: false,
		};
	}
}
